import { ApiError } from "@/shared/api/http-client";

/**
 * The NestJS API returns `{ message, error, statusCode }` on validation
 * failures — sometimes `message` is a string, sometimes an array of
 * per-field strings. Surface whatever the server sent so operators get
 * actionable feedback instead of a generic "check the content" fallback.
 *
 * Returns null when the body is empty, wrong shape, or the message is
 * an empty string — callers should fall back to a translated generic.
 */
export function extractApiErrorMessage(err: ApiError): string | null {
  const body = err.body;
  if (!body || typeof body !== "object") return null;

  const message = (body as { message?: unknown }).message;
  if (typeof message === "string") {
    const trimmed = message.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  if (Array.isArray(message)) {
    const joined = message
      .filter((m): m is string => typeof m === "string")
      .join("; ")
      .trim();
    return joined.length > 0 ? joined : null;
  }

  return null;
}

/**
 * Resolve any error thrown by a TanStack Query mutation to the user-facing
 * string to show in the dialog's error slot. Originally written for ToS,
 * now reused across every BO domain (catalog, feature flags, users, ...).
 * Keeps the precedence rules consistent so each call site only chooses
 * its own translation keys.
 *
 * Precedence:
 *  1. 401/403 → `forbiddenKey` (translated; server copy intentionally
 *     dropped to avoid leaking stack/role info).
 *  2. Any 4xx with a parseable server message → `serverPrefixKey`
 *     formatted with the server message (e.g. `Server: version ...`).
 *     Covers 400 validation, 409 conflicts (duplicate version, overlapping
 *     windows), 422 unprocessable, and anything else NestJS throws as a
 *     structured HttpException.
 *  3. 4xx without a message body → `genericKey` (stale generic; rare).
 *  4. 5xx / network / anything else → `genericKey`.
 *
 * The translator is passed in so we don't depend on a hook call site.
 */
export function resolveMutationErrorMessage(
  err: unknown,
  t: (key: string, values?: Record<string, unknown>) => string,
  keys: {
    readonly forbiddenKey: string;
    readonly serverPrefixKey: string;
    readonly genericKey: string;
  },
): string {
  if (err instanceof ApiError) {
    if (err.status === 401 || err.status === 403) return t(keys.forbiddenKey);
    if (err.status >= 400 && err.status < 500) {
      const serverMessage = extractApiErrorMessage(err);
      if (serverMessage) {
        return t(keys.serverPrefixKey, { message: serverMessage });
      }
    }
  }
  return t(keys.genericKey);
}
